import { of, from, concat, EMPTY } from "rxjs";
import * as R from "ramda";
import * as Promise from "bluebird";
import { api } from "../api/api";
import {
  mergeMap,
  withLatestFrom,
  map,
  filter,
  catchError,
  switchMap,
  debounceTime,
  takeUntil,
  expand,
} from "rxjs/operators";
import moment from "moment";
import { combineEpics, ofType } from "redux-observable";
import { datasources } from "../db/datasources";
import Notify from "../ui/Notify";
import {
  rememberScanJob,
  forgetScanJob,
  recallScanJob as recallScanJobFrom,
} from "./scanJobHandoff";

import i18n from "../i18n";
import uuid from "uuid/v1";
import {
  specsheetHandleForm,
  specsheetSetValues,
  specsheetUpdateSpecsheets,
  specsheetSetSpecsheets,
  specsheetPhotoSaved,
  specsheetPhotoScanned,
  specsheetPhotoScannedAI,
  specsheetScanPhotoAIError,
  specsheetScanResuming,
  specsheetBatchUploading,
  specsheetBatchQueued,
  specsheetBatchJobs,
  specsheetBatchError,
  specsheetCreditsLoaded,
  specsheetBatchReviewLoaded,
  specsheetBatchConfirmed,
  specsheetBatchCandidates,
  specsheetPhotoDeleted,
  specsheetSetOpenDropdown,
  specsheetSetThumbCuts,
  specsheetSetThumbCutsError,
  specsheetSetSpecsheet,
  specSheetRemoved,
  specsheetSelect,
  specSheetGetAuditRecordsSuccess,
  specsheetSVGClick,
  specSheetSendEmailDone,
  specSheetEmailSentError,
  specSheetShareToStatsDone,
  specSheetShareToStatsError,
  specSheetFetchStatsShares,
  specSheetFetchStatsSharesDone,
  setShowEmailConfirmModal,
  setShowSharingModal,
  types,
  SCAN_GUIDED_REASONS,
} from "./specsheet.redux";
import { types as bowlersTypes } from "../bowlers/bowlers.redux";
import { lpad } from "../../utils";
import {
  idx_data_pitch,
  idx_data_pitch_32,
  idx_data_pitch_16p,
  idx_data_pitch_64,
  idx_data_pitch_32p,
  idx_data_hole_sizes,
} from "../db/datafixtures/datafixtures";
import { createSpecsheet } from "./createSpecsheet";
import {
  splitScannedName,
  rankCandidates,
  confidentMatch,
} from "./scanNameMatch";
import { specSheetDecimalValues, toDecimal } from "../../utils";

// Poll pacing for a queued spec sheet scan. The ceiling is generous
// because the server's own budget already bounds the work; this only
// stops the UI waiting for a job that will never report back.
const SCAN_POLL_INTERVAL_MS = 2000;
// Must outlast the worker and stay under the store's stale marker:
// AnthropicClient::TIMEOUT (180s) < this (270s) < ScanJobStore::STALE_SECONDS (300s).
const SCAN_POLL_TIMEOUT_MS = 270000;

// The handoff shelf lives in its own module because the batch inbox writes to
// it too: it parks a finished job there and lets the review on the bowler's
// screen pick it up on mount.
const recallScanJob = () => recallScanJobFrom(SCAN_POLL_TIMEOUT_MS);

const pollScanJob = (ajaxSession, jobId, startedAt) => {
  const step = () =>
    ajaxSession(`specSheet/scanStatus/${jobId}`, {
      type: "GET",
      noWorker: true,
    }).then((job) => {
      if (!job || job.status === "done" || job.success === false) {
        forgetScanJob();
        return job;
      }
      if (Date.now() - startedAt > SCAN_POLL_TIMEOUT_MS) {
        forgetScanJob();
        return { success: false, message: i18n.t("specsheetScan.tooSlow") };
      }
      return new Promise((r) => setTimeout(r, SCAN_POLL_INTERVAL_MS)).then(
        step,
      );
    });
  return step();
};

Promise.config({ warnings: false, longStackTraces: false, asyncHooks: false });

const getSpecsheetData = (specsheetId) =>
  new Promise((success, reject) => {
    const { specSheetDS, thumbCutDS } = datasources;

    Promise.all([
      specSheetDS.get(specsheetId),
      thumbCutDS.read({ spec_sheet_id: specsheetId }),
    ])
      .then((response) => success(response))
      .catch(reject);
  });

const specsheetHandleFormEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetHandleForm),
    debounceTime(500),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { specsheetValues, noCloseDropdown, noMerge },
        },
        state,
      ]) =>
        from(
          new Promise((success) => {
            const selectedCustomerId = state.specsheet.currentSpecSheet
              .customer_id
              ? state.specsheet.currentSpecSheet.customer_id
              : state.bowlers.selectedCustomer.id;

            let specSheet = {};
            if (!noMerge) {
              specSheet = R.merge(
                {
                  ...state.specsheet.currentSpecSheet,
                  customer_id: selectedCustomerId,
                },
                R.omit(["id"])(specsheetValues),
              );
            } else {
              specSheet = {
                ...specsheetValues,
                customer_id: selectedCustomerId,
              };
            }

            const { specSheetDS } = datasources;

            specSheetDS.update(specSheet).then(success);
          }),
        ).pipe(
          map((specSheet) => {
            return specsheetSetValues({
              currentSpecSheet: specSheet,
              noCloseDropdown,
            });
          }),
        ),
    ),
  );

const specsheetSetValuesEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetSetValues),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { currentSpecSheet },
        },
        state,
      ]) => {
        const newSpecsheets = R.pipe(
          R.findIndex(R.propEq("id", currentSpecSheet.id)),
          R.update(
            R.__,
            { ...currentSpecSheet, thumb_cuts: state.specsheet.thumbCuts },
            state.specsheet.specsheets,
          ),
        )(state.specsheet.specsheets);
        return of(
          specsheetUpdateSpecsheets({
            specsheets: R.sort((s1, s2) => {
              const updateAtS1 =
                typeof s1.updated_at === "string"
                  ? moment(s1.updated_at).valueOf()
                  : s1.updated_at;
              const updateAtS2 =
                typeof s2.updated_at === "string"
                  ? moment(s2.updated_at).valueOf()
                  : s2.updated_at;
              return s1.updated_at && s2.updated_at
                ? updateAtS2 - updateAtS1
                : false;
            })(newSpecsheets),
          }),
        );
      },
    ),
  );

const specsheetDeletePhotoEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetDeletePhoto),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { deleteIndex },
        },
        state,
      ]) => {
        const filename = state.specsheet.currentSpecSheet.gallery[deleteIndex]
          .split("/")
          .pop();
        const { getSessionApiUrl, checkOffline, ajaxSession } = api;

        const removePhoto = (fileName) =>
          new Promise((success, fail) => {
            const deleteFileOnServer = (fileToDelete) =>
              R.pipeP(checkOffline, (offline) =>
                offline
                  ? Promise.resolve()
                  : ajaxSession(fileToDelete, {
                      type: "DELETE",
                    }),
              )();

            deleteFileOnServer(`sync/image/${fileName}`)
              .then(() => success())
              .catch(fail);
          });

        return from(removePhoto(filename)).pipe(
          map(() => {
            return specsheetPhotoDeleted({ deleteIndex });
          }),
        );
      },
    ),
  );

const specsheetSavePhotoEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetSavePhoto),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { imageBase64, extension },
        },
        state,
      ]) => {
        const processPhoto = () =>
          new Promise((success, fail) => {
            const { currentSpecSheet } = state.specsheet;
            const images = currentSpecSheet.gallery;

            let fileName = currentSpecSheet.id + `_0000.${extension}`;
            if (images.length > 0 && images[images.length - 1]) {
              let lastFileName = images[images.length - 1];
              if (lastFileName.indexOf("/") >= 0) {
                lastFileName = lastFileName.split("/")[1];
              }
              const lastFileNumber =
                lastFileName
                  .replace(`${currentSpecSheet.id}_`, "")
                  .split(".")[0] * 1;
              fileName = `${currentSpecSheet.id}_${lpad(
                lastFileNumber + 1,
                4,
              )}.${extension}`;
            }

            const { getSessionApiUrl, checkOffline, ajaxSession } = api;

            const sendImageToServer = ({ image, imageFileName }) =>
              R.pipeP(
                () => Promise.resolve("images/uploadImageBase64"),
                (url) => {
                  return ajaxSession(url, {
                    type: "POST",
                    noWorker: true,
                    data: {
                      image,
                      imageFileName,
                    },
                  });
                },
              )();

            const saveFileOnServer = (fileProps) =>
              R.pipeP(checkOffline, (offline) =>
                offline ? Promise.resolve() : sendImageToServer(fileProps),
              )();

            saveFileOnServer({ image: imageBase64, imageFileName: fileName })
              .then(() => {
                success(fileName);
              })
              .catch(fail);
          });
        return from(processPhoto()).pipe(
          map((photo) => {
            return specsheetPhotoSaved({ photo });
          }),
        );
      },
    ),
  );

const specsheetScanPhotoEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetScanPhoto),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { imageBase64, extension },
        },
        state,
      ]) => {
        const processPhoto = () =>
          new Promise((success, fail) => {
            const { currentSpecSheet } = state.specsheet;

            let fileName = currentSpecSheet.id + `_0000.${extension}`;

            const { getSessionApiUrl, checkOffline, ajaxSession } = api;

            const sendImageToServer = ({ image, imageFileName }) =>
              R.pipeP(
                () => Promise.resolve("images/uploadImageBase64"),
                (url) => {
                  return ajaxSession(url, {
                    type: "POST",
                    noWorker: true,
                    data: {
                      image,
                      imageFileName,
                    },
                  });
                },
              )();

            const saveFileOnServer = (fileProps) =>
              R.pipeP(checkOffline, (offline) =>
                offline ? Promise.resolve() : sendImageToServer(fileProps),
              )();

            saveFileOnServer({ image: imageBase64, imageFileName: fileName })
              .then(() => {
                success(fileName);
              })
              .catch(fail);
          });
        return from(processPhoto()).pipe(
          map((photo) => {
            return specsheetPhotoScanned({ photo });
          }),
        );
      },
    ),
  );

const specsheetScanPhotoAIEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetScanPhotoAI),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { imageBase64, extension },
        },
        state,
      ]) => {
        const processPhoto = () =>
          new Promise((success, fail) => {
            const { currentSpecSheet } = state.specsheet;

            let fileName = currentSpecSheet.id + `_0000.${extension}`;

            const { getSessionApiUrl, checkOffline, ajaxSession } = api;

            const sendImageToServer = ({ image, imageFileName }) =>
              R.pipeP(
                () => Promise.resolve("specSheet/uploadImageBase64"),
                (url) => {
                  return ajaxSession(url, {
                    type: "POST",
                    noWorker: true,
                    data: {
                      image,
                      imageFileName,
                    },
                  });
                },
              )();

            const saveFileOnServer = (fileProps) =>
              R.pipeP(checkOffline, (offline) =>
                offline ? Promise.resolve() : sendImageToServer(fileProps),
              )();

            // A scan takes 15-50s, past what Cloudflare will hold a request
            // open for, so the server queues it and hands back a job id. Older
            // servers answer with the values inline; both shapes are handled
            // so a frontend and backend deploy need not land together.
            // Held so it can be handed on: the review is a screen at its own
            // address now, and the address is the job id.
            let scanJobId = "";
            saveFileOnServer({ image: imageBase64, imageFileName: fileName })
              .then((response) => {
                if (response && response.jobId && !response.data) {
                  scanJobId = response.jobId;
                  rememberScanJob(response.jobId);
                  return pollScanJob(ajaxSession, response.jobId, Date.now());
                }
                return response;
              })
              .then((specsheet) => {
                success(
                  specsheet ? { ...specsheet, jobId: scanJobId } : specsheet,
                );
              })
              .catch(fail);
          });
        return from(processPhoto()).pipe(
          switchMap((scan) => {
            // The server answers 200 with success:false when it could not
            // read the sheet, so a failed scan arrives here, not in
            // catchError.
            if (!scan || !scan.success) {
              const message =
                (scan && scan.message) || i18n.t("specsheetScan.failed");
              const reason = (scan && scan.error) || "";
              // A toast is the wrong shape for a refusal the operator has to
              // act on: it says the bad half and then takes itself away. The
              // reason travels instead, and ScanLimitDialog answers it with
              // something that stays on screen and says what to do.
              if (SCAN_GUIDED_REASONS.indexOf(reason) === -1) {
                Notify.error(message);
              }
              return of(specsheetScanPhotoAIError({ message, reason }));
            }
            // Deliberately does NOT dispatch specsheetHandleForm here. The
            // values go to the review modal first; it applies them once the
            // operator has confirmed the fields the model flagged.
            return of(
              specsheetPhotoScannedAI({
                jobId: scan.jobId || "",
                data: scan.data || {},
                needsReview: scan.needsReview || [],
                notFound: scan.notFound || [],
                notes: scan.notes || "",
              }),
            );
          }),
          // Without this, one network failure completes the epic and every
          // later scan in the session silently does nothing.
          catchError((error) => {
            const message = i18n.t(
              error === "offline"
                ? "specsheetScan.offline"
                : "specsheetScan.failed",
            );
            Notify.error(message);
            return of(specsheetScanPhotoAIError({ message }));
          }),
        );
      },
    ),
  );

// Reattach to a scan that was still running when the page was reloaded. The
// server does not care that the browser went away; without this the finished
// result sits in the job store until it is swept.
const specsheetResumeScanEpic = (action$) =>
  action$.pipe(
    ofType(types.specsheetResumeScan),
    mergeMap(() => {
      const jobId = recallScanJob();
      if (!jobId) {
        return of();
      }
      const { ajaxSession } = api;
      // Spinner goes on here, not on the mount action: there is a job.
      return concat(
        of(specsheetScanResuming({})),
        from(pollScanJob(ajaxSession, jobId, Date.now())).pipe(
          switchMap((scan) => {
            if (!scan || !scan.success) {
              // Quietly drop it: the operator did not ask for this, so a toast
              // about a scan they may have abandoned would only confuse.
              return of(specsheetScanPhotoAIError({ message: "" }));
            }
            return of(
              specsheetPhotoScannedAI({
                jobId,
                data: scan.data || {},
                needsReview: scan.needsReview || [],
                notFound: scan.notFound || [],
                notes: scan.notes || "",
              }),
            );
          }),
          catchError(() => of(specsheetScanPhotoAIError({ message: "" }))),
        ),
      );
    }),
  );

// ------------------------------------------------------- batch scanning

const BATCH_POLL_INTERVAL_MS = 3000;

// A batch id keeps one inbox from showing another's rows. Hex so it matches
// what the server is willing to store.
const newBatchId = () =>
  uuid().replace(/-/g, "").slice(0, 32);

/**
 * Upload a pile of sheets one after another.
 *
 * Sequential on purpose: the server caps concurrent reads at four and queues
 * the rest, so firing forty uploads at once buys nothing and forty base64
 * payloads in flight together is exactly how a phone runs out of memory. The
 * queue is the thing that parallelises, not the upload.
 */
const specsheetBatchUploadEpic = (action$) =>
  action$.pipe(
    ofType(types.specsheetBatchUpload),
    switchMap(({ payload: { files } }) => {
      const batchId = newBatchId();
      const { ajaxSession } = api;

      const send = async () => {
        let done = 0;
        for (const file of files) {
          try {
            const response = await ajaxSession("specSheet/uploadImageBase64", {
              type: "POST",
              noWorker: true,
              data: {
                image: file.base64,
                imageType: file.mime,
                label: file.name,
                batchId,
              },
            });

            // A refusal arrives as HTTP 200 with success:false -- no
            // rejection, so neither the catch below nor a status check sees
            // it. Ignoring it is how running out of credits turned into an
            // inbox that stayed empty and said nothing: the worst possible
            // way to discover a sheet is metered.
            //
            // Stop the whole upload rather than trying the other thirty-nine.
            // Whatever refused the first one -- no credits, the monthly
            // ceiling -- will refuse every one after it, and thirty-nine more
            // identical refusals is not information.
            if (response && response.success === false) {
              // The reason travels with the message. "no_credits" is the one
              // the operator can do something about, and the screen answers
              // it with the packs rather than a sentence.
              return {
                done,
                stopped: response.message || "",
                reason: response.error || "",
              };
            }
          } catch (e) {
            // One unreadable file must not strand the other thirty-nine; it
            // simply never appears in the inbox.
          }
          done += 1;
        }
        return { done, stopped: "", reason: "" };
      };

      return concat(
        of(specsheetBatchUploading({ batchId, done: 0, total: files.length })),
        from(send()).pipe(
          mergeMap(({ stopped, reason }) =>
            stopped
              ? of(specsheetBatchError({ message: stopped, reason }))
              : of(specsheetBatchQueued({})),
          ),
          catchError(() =>
            of(specsheetBatchError({ message: i18n.t("specsheetScan.failed") })),
          ),
        ),
      );
    }),
  );

/**
 * Throw a row away, or the whole inbox.
 *
 * The store already dropped the row, so this only tells the server and puts it
 * back if the server disagrees. A sheet a worker is mid-read on can be
 * discarded like any other: the read is wasted, but the operator asked, and
 * the queue hands its slot on regardless.
 */
const specsheetBatchDiscardEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetBatchDiscard, types.specsheetBatchClear),
    mergeMap((action) => {
      const { ajaxSession } = api;
      const { batchId } = state$.value.specsheet;
      const clearing = action.type === types.specsheetBatchClear;
      const url = clearing
        ? "specSheet/scan/jobs"
        : `specSheet/scan/jobs/${action.payload.jobId}`;

      return from(
        ajaxSession(url, {
          type: "DELETE",
          noWorker: true,
          // params, not the path -- see the note in the poll epic. It carries
          // on a DELETE too, where a JSON body would not reach $request->get().
          params: clearing && batchId ? { batchId } : {},
        }),
      ).pipe(
        // Nothing to announce on success: the row is already gone.
        mergeMap(() => EMPTY),
        // ajaxSession rejects with the thrown error, not a status, so a job
        // the server had already swept cannot be told apart from a real
        // failure here. Both get the same answer: say so, and re-read the
        // inbox. If it was already gone the list simply agrees; if the delete
        // genuinely failed the row comes back rather than staying hidden.
        catchError(() =>
          of(
            specsheetBatchError({
              message: i18n.t("specsheetScan.batch.discardFailed"),
            }),
            specsheetBatchQueued({}),
          ),
        ),
      );
    }),
  );

/**
 * Poll the whole inbox while it is open.
 *
 * One request for all of it rather than one per sheet: forty queued sheets
 * would otherwise be forty requests every few seconds, and the reads are the
 * only thing here that should cost anything.
 */
const specsheetBatchPollEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetBatchOpen, types.specsheetBatchQueued),
    switchMap(() => {
      const { ajaxSession } = api;

      const step = () => {
        const { batchId } = state$.value.specsheet;
        // batchId goes through params, never appended to the path: the client
        // builds every request as `path + "?" + params`, so a query string
        // written here would meet a second "?" and the batch id would arrive
        // with the access token glued onto it. It then matched no batch, and
        // the inbox stayed empty until a reload cleared batchId.
        return ajaxSession("specSheet/scan/jobs", {
          type: "GET",
          noWorker: true,
          params: batchId ? { batchId } : {},
        })
          .then((response) => (response && response.jobs) || [])
          .catch(() => []);
      };

      return from(step()).pipe(
        // Keep asking while anything can still change, then stop: a settled
        // inbox is left alone rather than polled all afternoon.
        expand((jobs) =>
          jobs.some((j) => j.status === "pending" || j.status === "running")
            ? from(
                new Promise((r) => setTimeout(r, BATCH_POLL_INTERVAL_MS)).then(
                  step,
                ),
              )
            : EMPTY,
        ),
        map((jobs) => specsheetBatchJobs({ jobs })),
        takeUntil(action$.pipe(ofType(types.specsheetBatchClose))),
        catchError(() => of()),
      );
    }),
  );

/**
 * What the shop has left, and what it can buy.
 *
 * Normally one request. After a checkout it is a poll, because the credits
 * are not written by the checkout: Paddle notifies our webhook and the
 * webhook writes the ledger, which lands somewhere between immediately and
 * a few seconds later. Asking once the moment the overlay closes reliably
 * reads the balance from before the purchase, and the shop is told it still
 * has nothing after paying.
 *
 * The poll stops on the first increase, or gives up quietly. Giving up is not
 * an error worth a banner -- the money is taken and the webhook is idempotent,
 * so the credits turn up on the next read either way.
 */
const CREDITS_POLL_INTERVAL_MS = 2500;
const CREDITS_POLL_ATTEMPTS = 16;

const specsheetCreditsEpic = (action$) =>
  action$.pipe(
    ofType(types.specsheetCredits),
    switchMap(({ payload }) => {
      const { ajaxSession } = api;
      const awaitingAbove =
        payload && payload.awaitingAbove !== undefined
          ? payload.awaitingAbove
          : null;

      const read = () =>
        ajaxSession("specSheet/scan/credits", {
          type: "GET",
          noWorker: true,
          params: {},
        }).catch(() => null);

      return from(read().then((r) => ({ response: r, tries: 1 }))).pipe(
        expand(({ response, tries }) => {
          const stillBehind =
            awaitingAbove !== null &&
            (!response ||
              response.balance === null ||
              response.balance === undefined ||
              Number(response.balance) <= Number(awaitingAbove));

          if (!stillBehind || tries >= CREDITS_POLL_ATTEMPTS) return EMPTY;

          return from(
            new Promise((r) =>
              setTimeout(r, CREDITS_POLL_INTERVAL_MS),
            ).then(() => read().then((next) => ({ response: next, tries: tries + 1 }))),
          );
        }),
        filter(({ response }) => !!response && response.success !== false),
        map(({ response }) => {
          // Said out loud, and only when a purchase was being waited for.
          // The refusal panel closes itself the moment sheets arrive, and a
          // panel that vanishes without a word leaves the operator unsure
          // whether the money did anything.
          if (
            awaitingAbove !== null &&
            response.balance !== null &&
            response.balance !== undefined &&
            Number(response.balance) > Number(awaitingAbove)
          ) {
            Notify.success(
              i18n.t("specsheetScan.credits.added", {
                count: Number(response.balance),
              }),
            );
          }
          return specsheetCreditsLoaded(response);
        }),
        catchError(() => EMPTY),
      );
    }),
  );

/**
 * Bowlers who could be the person on this sheet.
 *
 * Searches by surname against the whole customer base rather than the page the
 * bowlers list happens to have loaded -- that limit is precisely what was
 * producing duplicates for regulars further down the list. The surname is used
 * because it is the discriminating half of a name and the half least likely to
 * have been read as a nickname.
 *
 * Failure is quiet and returns nothing: the operator then sees only "new
 * bowler", which is the same place they were before this existed.
 */
const findCandidates = async (scanned, proshopId) => {
  const { customerDS } = datasources;

  const search = async (term) => {
    if (!term) return [];
    try {
      const found = await customerDS.read({
        proshopId,
        filter: term,
        limit: 20,
        offset: 0,
        orderBy: "lastname, firstname",
      });
      return Array.isArray(found) ? found : [];
    } catch (e) {
      // Quiet on purpose: the operator then sees only "new bowler", which is
      // where they were before any of this existed.
      return [];
    }
  };

  const { lastname, firstname } = splitScannedName(scanned.name);
  const surname = lastname || firstname;

  // Searched by prefix, not by the whole surname. The server appends its own
  // "*", so asking for the first few letters is what survives the reader
  // dropping or inventing one at the end -- which is what a smudged sheet
  // produces, and what was quietly making duplicates.
  const stem = surname.length > 5 ? surname.slice(0, 4) : surname;

  const [byPhone, byName] = await Promise.all([
    search(String(scanned.phone || "").replace(/\D+/g, "")),
    search(stem),
  ]);

  const seen = {};
  const merged = [];
  for (const c of [...byPhone, ...byName]) {
    if (c && c.id && !seen[c.id]) {
      seen[c.id] = true;
      merged.push(c);
    }
  }

  return merged;
};

/**
 * Pull one finished read's values so the operator can look before deciding.
 *
 * The summary in the list is deliberately thin -- a name and a count -- so the
 * values are fetched only for the row actually being opened.
 */
const specsheetBatchReviewEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetBatchReview),
    withLatestFrom(state$),
    switchMap(([{ payload: { jobId } }, state]) => {
      const { ajaxSession } = api;
      const proshopId = state.userSession.proshopId;

      return from(
        ajaxSession(`specSheet/scanStatus/${jobId}`, {
          type: "GET",
          noWorker: true,
        }),
      ).pipe(
        switchMap((scan) => {
          if (!scan || !scan.success) {
            return of(
              specsheetBatchError({
                message:
                  (scan && scan.message) || i18n.t("specsheetScan.failed"),
              }),
            );
          }
          const values = scan.data || {};
          const contact = scan.contact || {};
          // Empty unless the sheet named a ball. The review offers it then,
          // and never otherwise.
          const ball = scan.ball || {};
          const bowler = scan.bowler || {};
          // The page image is fetched by the browser as an <img>, so what the
          // review needs is a signed URL, not the bytes.
          const previewUrl = scan.hasPreview
            ? api.getSessionApiUrl(`specSheet/scanPreview/${jobId}`, {})
            : Promise.resolve("");
          return concat(
            from(
              previewUrl
                // A missing image must not cost the review: it opens without
                // one, which is where it was before there was one at all.
                .catch(() => "")
                .then((url) =>
                  specsheetBatchReviewLoaded({
                    jobId,
                    values,
                    contact,
                    ball,
                    bowler,
                    previewUrl: url || "",
                    needsReview: scan.needsReview || [],
                    notFound: scan.notFound || [],
                    notes: scan.notes || "",
                  }),
                ),
            ),
            from(
              findCandidates(
                { name: values.name, phone: contact.phone },
                proshopId,
              ),
            ).pipe(
              map((found) => {
                const ranked = rankCandidates(found, {
                  name: values.name,
                  phone: contact.phone,
                });
                return specsheetBatchCandidates({
                  candidates: ranked,
                  exactId: confidentMatch(ranked),
                });
              }),
              catchError(() =>
                of(specsheetBatchCandidates({ candidates: [], exactId: "" })),
              ),
            ),
          );
        }),
        catchError(() =>
          of(specsheetBatchError({ message: i18n.t("specsheetScan.failed") })),
        ),
      );
    }),
  );

/**
 * Turn a confirmed read into a bowler's spec sheet, without leaving the inbox.
 *
 * Confirming has to be final here or the inbox is only half a tool: an
 * operator working down forty rows cannot be sent to another screen and back
 * for each one. So this does the whole thing -- find or create the bowler,
 * create the sheet, write the values -- and the row simply reports that it
 * landed.
 *
 * Nothing is created until the operator has confirmed, which is why this hangs
 * off the confirm action and not off the read finishing.
 */
const specsheetBatchConfirmEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetBatchConfirm),
    withLatestFrom(state$),
    mergeMap(
      (
        [
          {
            payload: {
              jobId,
              values,
              customerId,
              contact = {},
              ball = null,
              bowler = {},
              hasPage = false,
            },
          },
          state,
        ],
      ) => {
      const { customerDS, specSheetDS, ballDrilledDS } = datasources;
      const { ajaxSession } = api;
      const appSettings = state.settings.appSettings;

      // Where a record says it came from a scan.
      //
      // extra_values is free-form JSON that every SyncAudit table already
      // carries, so this costs no schema change -- and it is the only trace
      // that survives. The attached page is named "<sheet>-scan.png", but a
      // scan can finish without a page, attaching is deliberately non-fatal,
      // and deleting the attachment would take the evidence with it. The
      // audit tables record who and when, never from what.
      //
      // The job id is kept even though the job file is swept after 24 hours:
      // within that window it is what ties a record back to the read in the
      // logs, which is exactly when anyone goes looking.
      const scanMark = {
        scan: {
          at: new Date().toISOString(),
          job: jobId,
        },
      };

      const apply = async () => {
        let bowlerId = customerId;
        let created = null;

        if (!bowlerId) {
          const { firstname, lastname } = splitScannedName(values.name);
          // Named apart from the `bowler` argument on purpose. Calling this
          // one `bowler` too put the spread below inside its own
          // initialiser -- `const bowler = create({ ...bowler })` -- which is
          // a reference to a const that does not exist yet, so the call threw
          // before writing anything and every attempt to create a bowler from
          // a scan failed with "could not save the bowler".
          const saved = await customerDS.create({
            localId: uuid(),
            firstname,
            lastname,
            // Carried over from the sheet so the next scan for this person
            // finds them by phone rather than making a third record.
            telephone: contact.phone || "",
            email: contact.email || "",
            // The sheet's own hand if it was read, otherwise the shop default.
            hand: values.hand || appSettings.systemDefault.default_hand,
            style: "traditional",
            proshop_id: state.userSession.proshopId,
            // PAP, axis numbers and style belong to the person rather than the
            // sheet, and are only ever written on a bowler being created here.
            // An existing record's PAP was measured by somebody; a scan is not
            // grounds for overwriting it. Spread last so a style read from the
            // sheet wins over the default above.
            ...bowler,
            // Nothing to merge with: this record is a second old.
            extra_values: scanMark,
          });
          bowlerId = saved.id;
          created = saved;
        }

        // Held rather than generated inline: the datasource turns localId
        // into the record's id, so this is what the ball is hung off.
        const sheetId = uuid();
        const sheet = createSpecsheet(appSettings)({
          localId: sheetId,
          customerId: bowlerId,
          bowlerHand: values.hand || appSettings.systemDefault.default_hand,
        });

        // The scan wins over the defaults createSpecsheet fills in -- an
        // auto-filled bridge of 1/4 must not survive a sheet that says 1/8 --
        // but only where the scan actually read something.
        await specSheetDS.create({ ...sheet, ...values, extra_values: scanMark });

        // The paper this was read from, kept with the sheet it produced. A
        // transcription with no original is unarguable in the wrong direction
        // six months later, when the paper is in a filing cabinet somewhere
        // else.
        //
        // Only attempted when there is a page to keep. A scan can finish
        // without one -- a PDF Ghostscript could not rasterise, most likely --
        // and in that case the review showed no image and promised nothing, so
        // there is nothing to report either. Asking anyway would answer "no
        // preview" and turn a promise never made into an error.
        let attached = !hasPage;
        if (hasPage) {
          try {
            await ajaxSession(`specSheet/scan/jobs/${jobId}/attach`, {
              type: "POST",
              noWorker: true,
              data: { specSheetId: sheetId },
            });
            attached = true;
          } catch (e) {
            // A sheet with no picture is the sheet we had before this
            // existed, so the confirmation still stands -- but it is said out
            // loud rather than swallowed: the operator was told the page would
            // be kept, and silently not keeping it is how a promise becomes a
            // surprise.
          }
        }

        // Only when the operator left it ticked, and only ever with a name.
        // A sheet is often a re-drill or a page of measurements, and a ball
        // invented from a blank table is the kind of wrong nobody notices
        // until the arsenal is full of them.
        if (ball && ball.ball_name) {
          try {
            await ballDrilledDS.create({
              localId: uuid(),
              customer_id: bowlerId,
              spec_sheet_id: sheetId,
              enable: true,
              ...ball,
              extra_values: scanMark,
            });
          } catch (e) {
            // The bowler and the sheet are already saved and are the point of
            // this screen. Losing the ball is a line retyped in the arsenal;
            // failing the whole confirmation would lose the read as well.
          }
        }

        return { bowlerId, created, attached };
      };

      return from(apply()).pipe(
        mergeMap(({ bowlerId, created, attached }) =>
          concat(
            of(
              specsheetBatchConfirmed({
                jobId,
                customerId: bowlerId,
                customer: created,
              }),
            ),
            attached
              ? EMPTY
              : of(
                  specsheetBatchError({
                    message: i18n.t("specsheetScan.attachFailed"),
                  }),
                ),
          ),
        ),
        // Not specsheetScan.failed. What failed here is writing the bowler or
        // the sheet, and telling the operator we could not read their photo --
        // after showing them everything we read off it -- sends them to
        // re-scan a page that was read perfectly well. The error itself was
        // being dropped on the floor, which is why this one has never been
        // diagnosable from a report.
        catchError((error) => {
          console.error("scan review: could not apply", error);
          return of(
            specsheetBatchError({ message: i18n.t("specsheetScan.applyFailed") }),
          );
        }),
      );
      },
    ),
  );

const specsheetSVGClickEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetSVGClick),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { openDropdown },
        },
        state,
      ]) => {
        return from(
          new Promise((success) => {
            if (state.specsheet.tabChange) {
              setTimeout(() => success(openDropdown), 500);
            } else {
              success(openDropdown);
            }
          }),
        ).pipe(
          map((openDropdown) => specsheetSetOpenDropdown({ openDropdown })),
        );
      },
    ),
  );

const specsheetCalculateThumbCutsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetCalculateThumbCuts),
    withLatestFrom(state$),
    mergeMap(([_, state]) => {
      return from(
        new Promise((success, reject) => {
          const currentSpecSheet = state.specsheet.currentSpecSheet;
          if (
            !currentSpecSheet.thm_oval_starting_bit ||
            !currentSpecSheet.thm_oval_width ||
            !currentSpecSheet.thm_oval_degree
          ) {
            reject("No thumb info");
            return;
          }

          const { thumbCutDS, specSheetDS } = datasources;
          const lastSpecSheet = currentSpecSheet.id;

          let ovalWidth = 0;
          let startingBit = 0;

          startingBit = toDecimal(
            currentSpecSheet.thm_oval_starting_bit,
            state.settings.appSettings.systemDefault.span_measurement,
          );

          ovalWidth = toDecimal(
            currentSpecSheet.thm_oval_width,
            state.settings.appSettings.systemDefault.span_measurement,
          );

          specSheetDS
            .deleteFromFields({ id: lastSpecSheet, fieldName: "thumbCut" })
            .then(() => {
              let ovalDegree;
              let diff;
              let startV = 0;
              let startH = 0;
              let finalV;
              let finalH;
              let chunks;
              let chunks32;
              let extraCuts;
              let chunksRemain;
              let cut_number = 0;
              let chunkV;
              let chunkH;
              let i;
              let pitchV = 0;
              let pitchH = 0;
              let settingForRev = 1;
              let settingLateral = 1;

              if (
                !state.settings.appSettings.drillPressCalibration
                  .oval_machine_right_positive
              ) {
                settingLateral = -1;
              }

              if (
                !state.settings.appSettings.drillPressCalibration
                  .oval_machine_forward_thumb_positive
              ) {
                settingForRev = -1;
              }

              const thumbCutsToSave = [];

              const handMultiplier =
                currentSpecSheet.hand === "right_hander" ? 1 : -1;

              const pitch_measurement =
                state.settings.appSettings.systemDefault.pitch_measurement;
              const pitchDS = R.pathOr(idx_data_pitch, [pitch_measurement])({
                32: idx_data_pitch_32,
                "16+": idx_data_pitch_16p,
                64: idx_data_pitch_64,
                "32+": idx_data_pitch_32p,
              });

              if (
                state.settings.appSettings.thumbHolePreferences
                  .pitch_added_to_oval_calculation
              ) {
                if (currentSpecSheet.thm_rev !== "") {
                  const thm_rev_decimal = specSheetDecimalValues(
                    pitchDS,
                    currentSpecSheet.thm_rev,
                  );
                  if (thm_rev_decimal > 0) {
                    pitchV = thm_rev_decimal * -1 * settingForRev;
                  }
                }

                if (currentSpecSheet.thm_for !== "") {
                  const thm_for_decimal = specSheetDecimalValues(
                    pitchDS,
                    currentSpecSheet.thm_for,
                  );
                  if (thm_for_decimal > 0) {
                    pitchV = thm_for_decimal * settingForRev;
                  }
                }

                if (currentSpecSheet.thm_lat_r !== "") {
                  const thm_lat_r_decimal = specSheetDecimalValues(
                    pitchDS,
                    currentSpecSheet.thm_lat_r,
                  );
                  if (thm_lat_r_decimal > 0) {
                    pitchH = thm_lat_r_decimal * settingLateral;
                  }
                }

                if (currentSpecSheet.thm_lat_l !== "") {
                  const thm_lat_l_decimal = specSheetDecimalValues(
                    pitchDS,
                    currentSpecSheet.thm_lat_l,
                  );
                  if (thm_lat_l_decimal > 0) {
                    pitchH = thm_lat_l_decimal * -1 * settingLateral;
                  }
                }
              }

              if (
                state.settings.appSettings.thumbHolePreferences
                  .oval_calculation !== "manual"
              ) {
                //step 1
                ovalDegree = currentSpecSheet.thm_oval_degree * 1;
                if (
                  state.settings.appSettings.thumbHolePreferences
                    .oval_calculation === "horizontal"
                ) {
                  ovalDegree = 0;
                } else if (
                  state.settings.appSettings.thumbHolePreferences
                    .oval_calculation === "vertical"
                ) {
                  ovalDegree = 90;
                }
                diff = ovalWidth - startingBit;

                //step 2
                finalV = Math.sin(Math.radians(360 - ovalDegree)) * diff * -1;

                //step 3
                finalH =
                  Math.cos(Math.radians(360 - ovalDegree)) *
                  diff *
                  handMultiplier;

                //starting point
                if (
                  state.settings.appSettings.thumbHolePreferences
                    .pitch_center_edge === "center"
                ) {
                  startH = (finalH / 2) * settingLateral;
                  startV = (finalV / 2) * -1 * settingForRev;
                }

                finalV = pitchV - startV + finalV * -1 * settingForRev;
                finalH = pitchH - startH + finalH * settingLateral;

                //step 4
                chunks = Math.round(diff / (1 / 64));
                chunks32 = ~~(chunks / 2);
                extraCuts = chunks % 2;

                for (i = 0; i <= chunks32; i++) {
                  chunkV =
                    pitchV -
                    startV +
                    Math.sin(Math.radians(360 - ovalDegree)) *
                      (diff / chunks32) *
                      // (1 / 32) *
                      i *
                      -1 *
                      settingForRev *
                      -1;
                  if (handMultiplier > 0) {
                    chunkH =
                      pitchH -
                      startH +
                      Math.cos(Math.radians(360 - ovalDegree)) *
                        (diff / chunks32) *
                        // (1 / 32) *
                        i *
                        settingLateral;
                  } else {
                    chunkH =
                      pitchH -
                      startH -
                      Math.cos(Math.radians(360 - ovalDegree)) *
                        (diff / chunks32) *
                        // (1 / 32) *
                        i *
                        settingLateral;
                  }

                  cut_number++;
                  thumbCutsToSave.push({
                    cut_number: cut_number,
                    v: chunkV,
                    h: chunkH,
                    label: "" + cut_number,
                    spec_sheet_id: lastSpecSheet,
                  });
                }

                if (extraCuts > 0) {
                  chunkV =
                    pitchV -
                    startV +
                    Math.sin(Math.radians(360 - ovalDegree)) *
                      (diff / chunks) *
                      // (1 / 64) *
                      chunks *
                      -1 *
                      settingForRev *
                      -1;
                  if (handMultiplier > 0) {
                    chunkH =
                      pitchH -
                      startH +
                      Math.cos(Math.radians(360 - ovalDegree)) *
                        (diff / chunks) *
                        // (1 / 64) *
                        chunks *
                        settingLateral;
                  } else {
                    chunkH =
                      pitchH -
                      startH -
                      Math.cos(Math.radians(360 - ovalDegree)) *
                        (diff / chunks) *
                        // (1 / 64) *
                        chunks *
                        settingLateral;
                  }

                  if (
                    Math.abs(chunkV - finalV) > 1 / 64 ||
                    Math.abs(chunkH - finalH) > 1 / 64
                  ) {
                    cut_number++;
                    thumbCutsToSave.push({
                      cut_number: cut_number,
                      v: chunkV,
                      h: chunkH,
                      label: "" + cut_number,
                      spec_sheet_id: lastSpecSheet,
                    });
                  }
                }

                if (
                  Math.abs(chunkV - finalV) > 1 / 64 ||
                  Math.abs(chunkH - finalH) > 1 / 64
                ) {
                  cut_number++;
                  thumbCutsToSave.push({
                    cut_number: cut_number,
                    v: finalV,
                    h: finalH,
                    label: "" + cut_number,
                    spec_sheet_id: lastSpecSheet,
                  });
                }

                Promise.all(
                  thumbCutsToSave.map((thumbCut) =>
                    thumbCutDS.create(thumbCut),
                  ),
                ).then(success);
              }
            });
        }),
      ).pipe(
        map((thumbCuts) =>
          thumbCuts
            ? specsheetSetThumbCuts({ thumbCuts, openOvalInfo: false })
            : "",
        ),
        catchError((error) => of(specsheetSetThumbCutsError({ error }))),
      );
    }),
  );

const specsheetAddThumbCutEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetAddThumbCut),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { cutType },
        },
        state,
      ]) => {
        return from(
          new Promise((success, reject) => {
            const currentSpecSheet = state.specsheet.currentSpecSheet;
            const lastSpecSheet = currentSpecSheet.id;
            const thumbCuts = state.specsheet.thumbCuts;

            const firstCut = R.pipe(
              R.filter(
                (thumbCut) =>
                  thumbCut.label.indexOf("&#189;") < 0 &&
                  thumbCut.label.indexOf("&#188;") < 0,
              ),
              R.head,
            )(thumbCuts);

            const lastCut = R.pipe(
              R.filter(
                (thumbCut) =>
                  thumbCut.label.indexOf("&#189;") < 0 &&
                  thumbCut.label.indexOf("&#188;") < 0,
              ),
              R.last,
            )(thumbCuts);

            const realLastCut = R.last(thumbCuts);

            const cut_number = lastCut
              ? R.path(["cut_number"])(lastCut) * 1 + 1
              : 1;

            const real_cut_number = realLastCut
              ? R.path(["cut_number"])(realLastCut) * 1 + 1
              : 1;

            const { thumbCutDS } = datasources;

            switch (cutType) {
              case "new":
                const newCutData = {
                  cut_number,
                  v: 0,
                  h: 0,
                  label: "" + cut_number,
                  spec_sheet_id: lastSpecSheet,
                };

                thumbCutDS
                  .create(newCutData)
                  .then((newCutInsert) =>
                    success(R.append(newCutInsert)(thumbCuts)),
                  );
                break;
              case "half":
                if (lastCut) {
                  const halfCutData = {
                    cut_number: real_cut_number,
                    v: (lastCut.v * 1 + firstCut.v * 1) / 2,
                    h: (lastCut.h * 1 + firstCut.h * 1) / 2,
                    label: "&#189;",
                    spec_sheet_id: lastSpecSheet,
                  };

                  thumbCutDS
                    .create(halfCutData)
                    .then((halfCutInsert) =>
                      success(R.append(halfCutInsert)(thumbCuts)),
                    );
                } else {
                  reject("No cuts");
                }
                break;
              case "half_back":
                if (lastCut) {
                  const halfBackCutData = {
                    cut_number: real_cut_number,
                    v: lastCut.v,
                    h:
                      firstCut.h * 1 -
                      ((firstCut.h * 1 - lastCut.h * 1) * 1) / 2,
                    label: "&#189; Back",
                    spec_sheet_id: lastSpecSheet,
                  };

                  thumbCutDS
                    .create(halfBackCutData)
                    .then((halfBackCutInsert) =>
                      success(R.append(halfBackCutInsert)(thumbCuts)),
                    );
                } else {
                  reject("No cuts");
                }
                break;
              case "half_front":
                if (lastCut) {
                  const halfFrontCutData = {
                    cut_number: real_cut_number,
                    v: firstCut.v * 1 + (lastCut.v * 1 - firstCut.v * 1) / 2,
                    h: firstCut.h * 1,
                    label: "&#189; Front",
                    spec_sheet_id: lastSpecSheet,
                  };

                  thumbCutDS
                    .create(halfFrontCutData)
                    .then((halfFrontCutInsert) =>
                      success(R.append(halfFrontCutInsert)(thumbCuts)),
                    );
                } else {
                  reject("No cuts");
                }
                break;
              case "quarter_back":
                if (lastCut) {
                  const quarterBackCutData = {
                    cut_number: real_cut_number,
                    v: lastCut.v,
                    h:
                      firstCut.h * 1 -
                      ((firstCut.h * 1 - lastCut.h * 1) * 3) / 4,
                    label: "&#188; Back",
                    spec_sheet_id: lastSpecSheet,
                  };

                  thumbCutDS
                    .create(quarterBackCutData)
                    .then((quarterBackCutInsert) =>
                      success(R.append(quarterBackCutInsert)(thumbCuts)),
                    );
                } else {
                  reject("No cuts");
                }
                break;
              case "quarter_front":
                if (lastCut) {
                  const quarterFrontCutData = {
                    cut_number: real_cut_number,
                    v: firstCut.v * 1 + (lastCut.v * 1 - firstCut.v * 1) / 4,
                    h: firstCut.h * 1,
                    label: "&#188; Front",
                    spec_sheet_id: lastSpecSheet,
                  };

                  thumbCutDS
                    .create(quarterFrontCutData)
                    .then((quarterFrontCutInsert) =>
                      success(R.append(quarterFrontCutInsert)(thumbCuts)),
                    );
                } else {
                  reject("No cuts");
                }
                break;
            }
          }),
        ).pipe(
          map((thumbCuts) =>
            thumbCuts
              ? specsheetSetThumbCuts({
                  thumbCuts,
                  openOvalInfo: state.specsheet.isOvalPreviewOpen,
                })
              : "",
          ),
          catchError((error) => of(specsheetSetThumbCutsError({ error }))),
        );
      },
    ),
  );

const specsheetDeleteThumbCutEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetDeleteThumbCut),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { id },
        },
        state,
      ]) => {
        return from(
          new Promise((success, reject) => {
            const thumbCuts = state.specsheet.thumbCuts;

            const newThumbCuts = R.pipe(
              R.findIndex(R.propEq("id", id)),
              R.remove(R.__, 1, thumbCuts),
            )(thumbCuts);

            const { thumbCutDS } = datasources;

            thumbCutDS
              .delete(id)
              .then(() => success(newThumbCuts))
              .catch(reject);
          }),
        ).pipe(
          map((thumbCuts) =>
            thumbCuts
              ? specsheetSetThumbCuts({
                  thumbCuts,
                })
              : "",
          ),
          catchError((error) => of(specsheetSetThumbCutsError({ error }))),
        );
      },
    ),
  );

const specsheetUpdateThumbCutEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetUpdateThumbCut),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { id, v, h },
        },
        state,
      ]) => {
        return from(
          new Promise((success, reject) => {
            const thumbCuts = state.specsheet.currentSpecSheet.thumb_cuts;
            const thumbCut = R.find(R.propEq("id", id))(thumbCuts);

            const newThumbCut = { ...thumbCut, v, h };
            const newThumbCuts = R.pipe(
              R.findIndex(R.propEq("id", id)),
              R.update(R.__, newThumbCut, thumbCuts),
            )(thumbCuts);

            const { thumbCutDS } = datasources;

            thumbCutDS
              .update(newThumbCut)
              .then(() => success(newThumbCuts))
              .catch(reject);
          }),
        ).pipe(
          map((thumbCuts) =>
            thumbCuts
              ? specsheetSetThumbCuts({
                  thumbCuts,
                })
              : "",
          ),
          catchError((error) => of(specsheetSetThumbCutsError({ error }))),
        );
      },
    ),
  );

const specsheetAddNewEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetAddNew),
    withLatestFrom(state$),
    mergeMap(([_, state]) => {
      return from(
        new Promise((success, reject) => {
          const { specSheetDS } = datasources;
          const specsheets = state.specsheet.specsheets;
          const appSettings = state.settings.appSettings;
          const customerId = state.bowlers.selectedCustomer.id;
          const specsheetId = uuid();

          specSheetDS
            .create(
              createSpecsheet(appSettings)({
                localId: specsheetId,
                customerId,
                bowlerHand:
                  state.bowlers.selectedCustomer.hand !== ""
                    ? state.bowlers.selectedCustomer.hand
                    : state.settings.appSettings.systemDefault.default_hand,
              }),
            )
            .then((newSpecsheet) => {
              success({
                specsheets: R.append({
                  ...newSpecsheet,
                  customer_id: newSpecsheet.customer_id
                    ? newSpecsheet.customer_id
                    : (newSpecsheet.customer || {}).id,
                  thumb_cuts: [],
                })(specsheets),
                currentSpecSheetId: specsheetId,
                currentSpecSheet: {
                  ...newSpecsheet,
                  customer_id: newSpecsheet.customer_id
                    ? newSpecsheet.customer_id
                    : (newSpecsheet.customer || {}).id,
                  thumb_cuts: [],
                },
              });
            })
            .catch(reject);
        }),
      ).pipe(
        switchMap(({ specsheets, currentSpecSheet, currentSpecSheetId }) => {
          return concat(
            of(
              specsheetSetSpecsheets({
                specsheets: R.sort((s1, s2) => {
                  const updateAtS1 =
                    typeof s1.updated_at === "string"
                      ? moment(s1.updated_at).valueOf()
                      : s1.updated_at;
                  const updateAtS2 =
                    typeof s2.updated_at === "string"
                      ? moment(s2.updated_at).valueOf()
                      : s2.updated_at;
                  return s1.updated_at && s2.updated_at
                    ? updateAtS2 - updateAtS1
                    : false;
                })(specsheets),
              }),
            ),
            of(specsheetSetSpecsheet({ specsheet: currentSpecSheet })),
            of(
              specsheetSetValues({
                currentSpecSheet,
              }),
            ),
          );
        }),
        catchError((error) => {
          console.warn(error);
          return specsheetSetSpecsheets({
            specsheets: [],
            currentSpecSheet: null,
          });
        }),
      );
    }),
  );

const specsheetCloneEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetClone),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { specsheetToClone, thumbCuts, parts },
        },
        state,
      ]) => {
        return from(
          new Promise((success, reject) => {
            const { specSheetDS, thumbCutDS } = datasources;
            const specsheets = state.specsheet.specsheets;
            const specsheetId = uuid();

            // What the driller ticked in the clone dialog. Callers that
            // predate it (and the dialog's all-on default) copy everything,
            // which is what cloning always did.
            const cloneParts = parts || {};
            const cloneMeasurements = cloneParts.specsheet !== false;
            const cloneNote = cloneParts.note !== false;
            const cloneThumbCuts = cloneParts.thumbCuts !== false;

            // Unticking the sheet keeps the clone as a sheet for the same
            // bowler -- name, hand, grip, date, ownership -- with every
            // measurement blank, rather than producing nothing at all.
            //
            // Blanking the measurements rather than picking the survivors:
            // a whitelist silently drops whatever field gets added to the
            // sheet next, and some of those are structural, not measured.
            // thm_type and vacu stay because a sheet with no thumb type is
            // not blank, it is broken, and the grip types go back to what
            // createSpecsheet gives a brand-new sheet.
            const isMeasurement = (key) =>
              (key.indexOf("f1_") === 0 ||
                key.indexOf("f2_") === 0 ||
                key.indexOf("thm_") === 0 ||
                key === "bridge" ||
                key === "clt") &&
              key !== "thm_type";

            const measurementsOf = (sheet) =>
              cloneMeasurements
                ? sheet
                : {
                    ...R.mapObjIndexed(
                      (val, key) => (isMeasurement(key) ? "" : val),
                      sheet
                    ),
                    f1_grip_type: "No inserts",
                    f2_grip_type: "No inserts",
                  };

            specSheetDS
              .create(
                R.pipe(
                  measurementsOf,
                  R.omit(["id", "created_at", "updated_at"]),
                  cloneNote ? R.identity : R.assoc("note", ""),
                  R.assoc("localId", specsheetId),
                  R.assoc(
                    "name",
                    `${R.prop("name")(specsheetToClone)} <Clone>`,
                  ),
                )(specsheetToClone),
              )
              .then((newSpecsheet) => {
                return Promise.all(
                  (cloneThumbCuts ? thumbCuts || [] : []).map((thumbCut) =>
                    thumbCutDS.create(
                      R.pipe(
                        R.omit("id"),
                        R.assoc("spec_sheet_id", specsheetId),
                      )(thumbCut),
                    ),
                  ),
                ).then((createdThumbcuts) => {
                  success({
                    specsheets: R.append({
                      ...newSpecsheet,
                      customer_id: newSpecsheet.customer_id
                        ? newSpecsheet.customer_id
                        : (newSpecsheet.customer || {}).id,
                      thumb_cuts: createdThumbcuts,
                    })(specsheets),
                    currentSpecSheetId: specsheetId,
                    currentSpecSheet: {
                      ...newSpecsheet,
                      customer_id: newSpecsheet.customer_id
                        ? newSpecsheet.customer_id
                        : (newSpecsheet.customer || {}).id,
                      thumb_cuts: createdThumbcuts,
                    },
                  });
                });
              });
          }),
        ).pipe(
          switchMap(({ specsheets, currentSpecSheet, currentSpecSheetId }) => {
            return concat(
              of(
                specsheetSetSpecsheets({
                  specsheets: R.sort((s1, s2) => {
                    const updateAtS1 =
                      typeof s1.updated_at === "string"
                        ? moment(s1.updated_at).valueOf()
                        : s1.updated_at;
                    const updateAtS2 =
                      typeof s2.updated_at === "string"
                        ? moment(s2.updated_at).valueOf()
                        : s2.updated_at;
                    return s1.updated_at && s2.updated_at
                      ? updateAtS2 - updateAtS1
                      : false;
                  })(specsheets),
                }),
              ),
              of(
                specsheetSetSpecsheet({
                  specsheet: currentSpecSheet,
                }),
              ),
              of(
                specsheetSetValues({
                  currentSpecSheet,
                }),
              ),
              of(specsheetSVGClick({ openDropdown: "name" })),
            );
          }),
          catchError((error) => {
            console.warn(error);
            return specsheetSetSpecsheets({
              specsheets: [],
              currentSpecSheet: {},
            });
          }),
        );
      },
    ),
  );

const specsheetBowlerSelectEpic = (action$, state$) =>
  action$.pipe(
    ofType(bowlersTypes.bowlerSelect),
    mergeMap(() =>
      of(specsheetSetSpecsheet({ specsheet: null, thumbCuts: [] })),
    ),
  );

const specsheetSelectEpic = combineEpics(
  specsheetResumeScanEpic,
  (action$) =>
    action$.pipe(
      ofType(types.specsheetSetSpecsheets),
      mergeMap(({ payload: { currentSpecSheet } }) => {
        return currentSpecSheet
          ? from(getSpecsheetData(currentSpecSheet)).pipe(
              map(([specsheet, thumbCuts]) =>
                specsheetSetSpecsheet({
                  specsheet: {
                    ...specsheet,
                    thumb_cuts: thumbCuts,
                    customer_id: specsheet.customer_id
                      ? specsheet.customer_id
                      : (specsheet.customer || {}).id,
                  },
                  thumbCuts,
                }),
              ),
            )
          : of(
              specsheetSetSpecsheet({
                specsheet: null,
                thumbCuts: [],
              }),
            );
      }),
    ),
  (action$) =>
    action$.pipe(
      ofType(types.specsheetSelect),
      mergeMap(({ payload: { specsheet } }) => {
        return specsheet
          ? from(getSpecsheetData(specsheet.id)).pipe(
              map(([specsheet, thumbCuts]) =>
                specsheetSetSpecsheet({ specsheet, thumbCuts }),
              ),
            )
          : of(
              specsheetSetSpecsheet({
                specsheet: null,
                thumbCuts: [],
              }),
            );
      }),
    ),
);

const specsheetRemoveEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specSheetRemove),
    mergeMap(({ payload: { id } }) => {
      const { specSheetDS } = datasources;
      return from(Promise.all([specSheetDS.delete(id)])).pipe(
        switchMap(() => {
          const { specsheets, currentSpecSheet } = state$.value.specsheet;
          const filterSpecsheets = specsheets.filter(
            (specsheetTemp) => specsheetTemp.id !== id,
          );
          if (currentSpecSheet.id !== id) {
            return of(specSheetRemoved({ specsheets: filterSpecsheets }));
          } else if (filterSpecsheets.length === 0) {
            return of(specSheetRemoved({ specsheets: [] }));
          } else {
            return concat(
              of(
                specsheetSelect({
                  specsheet: filterSpecsheets[0] || {},
                }),
              ),
              of(specSheetRemoved({ specsheets: filterSpecsheets })),
            );
          }
        }),
      );
    }),
  );

const specsheetGetAuditRecordsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specSheetGetAuditRecords),
    mergeMap(({ payload: { id } }) => {
      const { checkOffline, ajaxSession } = api;

      const getAuditRecords = () =>
        new Promise((success, fail) => {
          const auditRecordsFromServer = (auditUrl, params) =>
            R.pipeP(checkOffline, (offline) =>
              offline
                ? Promise.resolve()
                : ajaxSession(auditUrl, {
                    type: "GET",
                    params,
                  }),
            )();

          auditRecordsFromServer(`audit/spec_sheet/${id}`)
            .then((specsheetAuditRecords) => {
              return auditRecordsFromServer(`audit/thumb_cut/${id}`, {
                key_name: "spec_sheet_id",
              }).then((thumbcutsAuditRecords) => {
                const proccessedAuditRecords = R.groupWith(
                  (a, b) =>
                    a.revtype === b.revtype &&
                    Math.round(a.updated_at / 100000) ===
                      Math.round(b.updated_at / 100000),
                  thumbcutsAuditRecords,
                );

                return {
                  specsheetAuditRecords,
                  thumbcutsAuditRecords: proccessedAuditRecords,
                };
              });
            })
            .then(success)
            .catch(fail);
        });

      return from(getAuditRecords(id)).pipe(
        map(({ specsheetAuditRecords, thumbcutsAuditRecords }) => {
          return specSheetGetAuditRecordsSuccess({
            specsheetAuditRecords,
            thumbcutsAuditRecords,
          });
        }),
      );
    }),
  );

const specsheetTriggerEmailEpic = (action$) =>
  action$.pipe(
    ofType(types.specSheetSetFileName),
    withLatestFrom(action$),
    mergeMap(
      ([
        {
          payload: { email, specSheetId, fileName },
        },
        action$,
      ]) => {
        if (fileName) {
          return of(specsheetSendEmailEpic({ email, specSheetId, fileName }));
        } else {
          return of({ type: "NO_OP" });
        }
      },
    ),
  );

const specsheetSendEmailEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specSheetSendEmail),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { email, fileName, file },
        },
        state,
      ]) => {
        const { ajaxSession } = api;

        // Create JSON payload
        const payload = {
          email,
          emailCC: state.userSession.userData?.email
            ? state.userSession.userData?.email
            : state.useSession.userData?.username, // CC to the user's email
          pdfFile: file, // Base64-encoded file content
          pdfFileName: fileName, // File name
          proshopName: state.userSession.proshop?.name,
          proshopPhone: state.userSession.userData?.phone,
          bowlerName: state.bowlers.selectedCustomer?.firstname,
        };
        console.log("Sending email with payload:", payload);

        // Make the API call using ajaxSession
        return from(
          ajaxSession("specSheet/sendSpecSheetEmail", {
            type: "POST",
            data: payload, // Send JSON payload
            contentType: "application/json", // Set Content-Type to JSON
            noWorker: true,
          }),
        ).pipe(
          mergeMap((response) =>
            of(
              specSheetSendEmailDone({
                response: response.data, // Access the response data
              }),
              setShowEmailConfirmModal({
                showEmailConfirmModal: true, // Show confirmation modal
              }),
            ),
          ),
          catchError((error) =>
            of(
              specSheetEmailSentError({
                error: error.response?.message || error?.message,
              }),
            ),
          ),
        );
      },
    ),
  );

// Spectre Connect: share the ball drilled on the current spec sheet (+ the
// sheet snapshot) to the customer's Spectre Stats app. The server resolves
// the ball and checks ownership; 200 only means the event was queued.
const shareToStatsErrorKey = (status) => {
  switch (status) {
    case 503:
      return [
        "form.label.error_spectre_stats_not_configured",
        "Spectre Stats sharing is not enabled on this server yet.",
      ];
    case 404:
      return [
        "form.label.error_spectre_stats_no_ball",
        "This spec sheet has no drilled ball to share.",
      ];
    case 400:
      return [
        "form.label.error_enter_email_for_customer",
        "Please enter an email for your customer",
      ];
    default:
      return [
        "form.label.error_sending_to_spectre_stats",
        "We couldn't send this to Spectre Stats. Please try again.",
      ];
  }
};

const specsheetShareToStatsEpic = (action$) =>
  action$.pipe(
    ofType(types.specSheetShareToStats),
    mergeMap(({ payload: { specSheetId, specSheet, operationId } }) => {
      const { ajaxSession } = api;

      return from(
        ajaxSession("spectreConnect/shareBall", {
          type: "POST",
          data: { specSheetId, specSheet, operationId },
          contentType: "application/json",
          noWorker: true,
        }),
      ).pipe(
        mergeMap((response) => {
          // Twix resolves the parsed body itself (no .data wrapper)
          const delivered = response?.delivered === true;
          Notify.success(
            delivered
              ? i18n.t(
                  "form.label.sent_to_spectre_stats",
                  "Sent! Your customer will get it in Spectre Stats.",
                )
              : i18n.t(
                  "form.label.queued_to_spectre_stats",
                  "Queued. Your customer will get it in Spectre Stats shortly.",
                ),
          );
          return of(
            specSheetShareToStatsDone({ delivered }),
            specSheetFetchStatsShares({ specSheetId }),
            setShowSharingModal({ showSharingModal: false }),
          );
        }),
        catchError((error) => {
          // ajax() rejects with the XMLHttpRequest, so the HTTP status is here
          const status = typeof error?.status === "number" ? error.status : 0;
          const [key, fallback] = shareToStatsErrorKey(status);
          Notify.error(i18n.t(key, fallback));
          return of(specSheetShareToStatsError({ status }));
        }),
      );
    }),
  );

// Spectre Connect: what happened to the balls already sent from this spec
// sheet (queued / delivered / accepted by the customer in Spectre Stats).
// Informational only: any failure just leaves the status line empty.
const specsheetFetchStatsSharesEpic = (action$) =>
  action$.pipe(
    ofType(types.specSheetFetchStatsShares),
    switchMap(({ payload: { specSheetId } }) => {
      const { ajaxSession } = api;
      return from(
        ajaxSession(
          `spectreConnect/ballShares?specSheetId=${encodeURIComponent(specSheetId)}`,
          { type: "GET", noWorker: true },
        ),
      ).pipe(
        map((response) =>
          specSheetFetchStatsSharesDone({
            specSheetId,
            shares: Array.isArray(response?.shares) ? response.shares : [],
          }),
        ),
        catchError(() =>
          of(specSheetFetchStatsSharesDone({ specSheetId, shares: [] })),
        ),
      );
    }),
  );

export default combineEpics(
  specsheetBowlerSelectEpic,
  specsheetHandleFormEpic,
  specsheetDeletePhotoEpic,
  specsheetSavePhotoEpic,
  specsheetScanPhotoEpic,
  specsheetScanPhotoAIEpic,
  specsheetSVGClickEpic,
  specsheetBatchUploadEpic,
  specsheetBatchDiscardEpic,
  specsheetBatchPollEpic,
  specsheetCreditsEpic,
  specsheetBatchReviewEpic,
  specsheetBatchConfirmEpic,
  specsheetCalculateThumbCutsEpic,
  specsheetAddThumbCutEpic,
  specsheetDeleteThumbCutEpic,
  specsheetUpdateThumbCutEpic,
  specsheetAddNewEpic,
  specsheetCloneEpic,
  specsheetSelectEpic,
  specsheetSendEmailEpic,
  specsheetShareToStatsEpic,
  specsheetFetchStatsSharesEpic,
  specsheetTriggerEmailEpic,
  specsheetSetValuesEpic,
  specsheetRemoveEpic,
  specsheetGetAuditRecordsEpic,
);
